import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack,
  Chip, Alert, LinearProgress, Grid, InputAdornment, Rating, Tooltip,
  Tabs, Tab,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { VENDORS_QUERY, VENDOR_AVAILABILITY_QUERY } from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import VendorAccountsPanel from '../components/VendorAccountsPanel';

const CREATE_VENDOR_MUTATION = gql`
  mutation CreateVendor($input: VendorInput!) {
    createVendor(input: $input) {
      id name vendorType city priceMin priceMax pricingModel isActive averageRating reviewCount
    }
  }
`;

const UPDATE_VENDOR_MUTATION = gql`
  mutation UpdateVendor($id: ID!, $input: VendorInput!) {
    updateVendor(id: $id, input: $input) {
      id name vendorType city priceMin priceMax pricingModel isActive
    }
  }
`;

const DELETE_VENDOR_MUTATION = gql`
  mutation DeleteVendor($id: ID!) { deleteVendor(id: $id) }
`;

const SET_VENDOR_AVAILABILITY_MUTATION = gql`
  mutation SetVendorAvailability($vendorId: ID!, $eventDate: String!, $isAvailable: Boolean!, $notes: String) {
    setVendorAvailability(vendorId: $vendorId, eventDate: $eventDate, isAvailable: $isAvailable, notes: $notes) {
      id eventDate isAvailable notes
    }
  }
`;

const emptyVendor = {
  name: '', vendorType: '', city: '', state: '', contactName: '', contactPhone: '',
  contactEmail: '', website: '', priceMin: '', priceMax: '', pricingModel: '', description: '',
};

function VendorAvailabilityDialog({ vendor, open, onClose }) {
  const [date, setDate]       = useState('');
  const [available, setAvail] = useState(false);
  const [notes, setNotes]     = useState('');

  const from = new Date().toISOString().slice(0, 10);
  const { data, refetch } = useQuery(VENDOR_AVAILABILITY_QUERY, {
    variables: { vendorId: vendor?.id, fromDate: from },
    skip: !vendor?.id,
  });
  const [setVendorAvail] = useMutation(SET_VENDOR_AVAILABILITY_MUTATION, {
    onCompleted: () => { setDate(''); setNotes(''); refetch(); },
  });

  const records = data?.vendorAvailability || [];
  const blocked = records.filter(r => !r.isAvailable);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Availability — {vendor?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>Mark a date as unavailable (or available again)</Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField label="Date" type="date" value={date} onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
          <TextField select label="Available?" value={available ? 'yes' : 'no'}
            onChange={e => setAvail(e.target.value === 'yes')} size="small" sx={{ width: 130 }}>
            <MenuItem value="yes">Available</MenuItem>
            <MenuItem value="no">Unavailable</MenuItem>
          </TextField>
          <TextField label="Notes" value={notes} onChange={e => setNotes(e.target.value)} size="small" sx={{ flex: 1 }} />
          <Button variant="contained" size="small"
            onClick={() => setVendorAvail({ variables: { vendorId: vendor?.id, eventDate: date, isAvailable: available, notes: notes || null } })}
            disabled={!date}>Save</Button>
        </Stack>

        {blocked.length > 0 ? (
          <>
            <Typography variant="subtitle2" gutterBottom>Booked / Unavailable Dates</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Date</TableCell><TableCell>Notes</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {blocked.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.eventDate}</TableCell>
                    <TableCell>{r.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          <Alert severity="success">No upcoming unavailable dates marked.</Alert>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// The earlier vendor directory (families' Vendors page reads it), kept as its own tab
function LegacyVendorDirectory() {
  const { options: vendorTypeOpts } = useLov('vendor_type');
  const { options: pricingOpts }    = useLov('pricing_model');
  const { data, loading, error, refetch } = useQuery(VENDORS_QUERY, { variables: {}, fetchPolicy: 'cache-and-network' });

  const [form, setForm]         = useState(emptyVendor);
  const [editId, setEditId]     = useState(null);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [availVendor, setAvailVendor] = useState(null);

  const [createVendor] = useMutation(CREATE_VENDOR_MUTATION, { onCompleted: () => { setDialogOpen(false); setForm(emptyVendor); refetch(); } });
  const [updateVendor] = useMutation(UPDATE_VENDOR_MUTATION, { onCompleted: () => { setDialogOpen(false); refetch(); } });
  const [deleteVendor] = useMutation(DELETE_VENDOR_MUTATION, { onCompleted: refetch });

  const vendors = data?.vendors || [];

  const openCreate = () => { setForm(emptyVendor); setEditId(null); setDialogOpen(true); };
  const openEdit   = (v) => {
    setForm({
      name: v.name || '', vendorType: v.vendorType || '', city: v.city || '', state: v.state || '',
      contactName: v.contactName || '', contactPhone: v.contactPhone || '',
      contactEmail: v.contactEmail || '', website: v.website || '',
      priceMin: v.priceMin || '', priceMax: v.priceMax || '',
      pricingModel: v.pricingModel || '', description: v.description || '',
    });
    setEditId(v.id);
    setDialogOpen(true);
  };

  const buildInput = () => ({
    ...form,
    priceMin: form.priceMin ? parseFloat(form.priceMin) : null,
    priceMax: form.priceMax ? parseFloat(form.priceMax) : null,
  });

  const handleSave = () => {
    if (editId) updateVendor({ variables: { id: editId, input: buildInput() } });
    else        createVendor({ variables: { input: buildInput() } });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Vendor directory</Typography>
          <Typography variant="body2" color="text.secondary">Add and manage service providers available for booking</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Vendor</Button>
      </Stack>

      {loading && <LinearProgress />}
      {error   && <Alert severity="error">{error.message}</Alert>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>City</TableCell>
            <TableCell>Price Range (₹)</TableCell>
            <TableCell>Rating</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vendors.map(v => (
            <TableRow key={v.id} hover>
              <TableCell><Typography fontWeight={600}>{v.name}</Typography>{v.contactPhone && <Typography variant="caption" color="text.secondary"> · {v.contactPhone}</Typography>}</TableCell>
              <TableCell><Chip label={v.vendorType?.replace('_', ' ')} size="small" variant="outlined" color="secondary" /></TableCell>
              <TableCell>{v.city || '—'}</TableCell>
              <TableCell>
                {v.priceMin || v.priceMax
                  ? `₹${v.priceMin ? Number(v.priceMin).toLocaleString('en-IN') : '0'} – ₹${v.priceMax ? Number(v.priceMax).toLocaleString('en-IN') : '?'}`
                  : '—'}
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Rating value={v.averageRating || 0} readOnly size="small" precision={0.1} />
                  <Typography variant="caption">({v.reviewCount})</Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip label={v.isActive ? 'Active' : 'Inactive'} size="small" color={v.isActive ? 'success' : 'default'} />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Manage Availability">
                  <IconButton size="small" onClick={() => setAvailVendor(v)}><EventBusyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error"
                  onClick={() => window.confirm('Delete this vendor?') && deleteVendor({ variables: { id: v.id } })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {vendors.length === 0 && !loading && (
            <TableRow><TableCell colSpan={7} align="center">No vendors yet. Click "Add Vendor" to get started.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField label="Vendor / Business Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Vendor Type" value={form.vendorType} onChange={e => setForm(f => ({ ...f, vendorType: e.target.value }))} fullWidth required>
                <MenuItem value="">— Select —</MenuItem>
                {vendorTypeOpts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Contact Name" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Contact Phone" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Contact Email" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={4}>
              <TextField label="Min Price" type="number" value={form.priceMin}
                onChange={e => setForm(f => ({ ...f, priceMin: e.target.value }))} fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Max Price" type="number" value={form.priceMax}
                onChange={e => setForm(f => ({ ...f, priceMax: e.target.value }))} fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField select label="Pricing Model" value={form.pricingModel} onChange={e => setForm(f => ({ ...f, pricingModel: e.target.value }))} fullWidth>
                <MenuItem value="">— Select —</MenuItem>
                {pricingOpts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description / Portfolio Notes" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.vendorType}>{editId ? 'Save Changes' : 'Add Vendor'}</Button>
        </DialogActions>
      </Dialog>

      {/* Availability Dialog */}
      <VendorAvailabilityDialog vendor={availVendor} open={Boolean(availVendor)} onClose={() => setAvailVendor(null)} />
    </Box>
  );
}

// Admin > Vendors: vendor accounts (from Users) with their services, plus the older directory
export default function AdminVendorsPage() {
  const [tab, setTab] = useState('accounts');
  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>Vendors</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="accounts" label="Vendor accounts" />
        <Tab value="directory" label="Vendor directory (families' Vendors page)" />
      </Tabs>
      {tab === 'accounts' ? <VendorAccountsPanel /> : <LegacyVendorDirectory />}
    </Box>
  );
}
